/**
 * OPS-UAT-002 — classify API errors and guard duplicate operator actions (no workflow engine).
 */

export const POST_COLLECT_COOLDOWN_MS = 1500;

export const STALE_WORKSPACE_REFRESH_MS = 60_000;

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isStaleWorkspaceConflict(error) {
  return matchesOperationalError(error, [
    'VERSION MISMATCH',
    'CHANGED SINCE',
    'BIL_OP_CONFLICT: STALE',
    'BIL_DISC_STALE',
    'BIL_RCP_STALE',
    'NOT OPEN FOR PAYMENT',
    'INVOICE IS NOT OPEN',
  ]);
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isDuplicateOperationalAction(error) {
  return matchesOperationalError(error, [
    'DUPLICATE',
    'ALREADY POSTED',
    'ALREADY FULFILLED',
    'ALREADY VERIFIED',
    'IDEMPOTENT',
    'NOT PENDING',
  ]);
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isRecoverableOperationalError(error) {
  return (
    isStaleWorkspaceConflict(error) ||
    isDuplicateOperationalAction(error) ||
    isRefundReviewInvalidStateMessage(extractOperationalErrorMessage(error))
  );
}

/**
 * @param {unknown} error
 * @param {'stale' | 'duplicate' | 'network' | 'unknown'} [kind]
 */
const CONNECTION_INTERRUPTED =
  'Connection was interrupted. Refresh before retrying — do not tap Collect again until you verify the bill.';
const BILL_CHANGED =
  'This bill was changed by another action. Refresh the bill before trying again.';
const BILLING_UNAVAILABLE =
  'The billing service is temporarily unavailable. Your bill has not been lost. Please try again.';
const CHECK_INFORMATION = 'Please check the entered information.';

export function operatorRecoveryMessage(error, kind) {
  const raw = extractOperationalErrorMessage(error);
  const status = Number(error?.response?.status ?? error?.status) || 0;
  const known = mapCashierBillingError(raw);
  if (known) return known;

  if (kind === 'network' || isNetworkOperationalError(error)) {
    return CONNECTION_INTERRUPTED;
  }
  if (kind === 'stale' || isStaleWorkspaceConflict(error)) {
    return BILL_CHANGED;
  }
  if (kind === 'duplicate' || isDuplicateOperationalAction(error)) {
    return 'This action may already be complete. Refresh to see the latest state.';
  }
  if (isRefundReviewInvalidStateMessage(raw)) {
    return raw;
  }
  if (status === 500 || status === 502 || status === 503 || status === 504) {
    if (isBillingError(error, raw) || isUnsafeCashierText(raw)) {
      return isBillingError(error, raw) || !hasForeignApiPath(error)
        ? BILLING_UNAVAILABLE
        : 'The service is temporarily unavailable. Please try again.';
    }
  }
  if (
    (status === 400 || status === 422) &&
    (!raw || isUnsafeCashierText(raw) || /^validation error$/i.test(String(raw).trim()))
  ) {
    const detail = detailAfterCode(raw);
    if (detail) return detail;
    return CHECK_INFORMATION;
  }
  if (isUnsafeCashierText(raw)) {
    if (!raw && !status) return 'Action failed. Refresh the workspace before retrying.';
    const detail = detailAfterCode(raw);
    if (detail && status < 500) return detail;
    if (status >= 500) return BILLING_UNAVAILABLE;
    if (status === 409) {
      return 'This action may already be complete. Refresh to see the latest state.';
    }
    return CHECK_INFORMATION;
  }
  return raw || 'Action failed. Refresh the workspace before retrying.';
}

function mapCashierBillingError(raw) {
  const upper = String(raw || '').toUpperCase();
  if (!upper) return '';
  if (
    upper.includes('BIL_CTR_CLOSED_SESSION') ||
    upper.includes('BIL_SHIFT_LOCKED') ||
    upper.includes('MUST BE ACTIVE') ||
    /SHIFT \d+ IS LOCKED/.test(upper)
  ) {
    return 'Your cashier shift is closed. Open a shift before collecting payment.';
  }
  if (upper.includes('DOES NOT MATCH THE PHARMACY TOTAL') || upper.includes("ADMINISTRATOR'S REVIEW BEFORE IT CAN BE COMPLETED")) {
    return "This sale needs an administrator's review before it can be completed.";
  }
  if (
    upper.includes('NO MEDICINE LINES') ||
    upper.includes('SPLIT ACROSS INVOICES') ||
    upper.includes('DIFFERENT PHARMACY SALE') ||
    upper.includes('DIFFERENT BILLING ACCOUNT') ||
    upper.includes('NEEDS A BILLING CHECK')
  ) {
    return 'This sale needs a billing check before payment. Your medicines are saved. Please contact your administrator.';
  }
  if (
    upper.includes('PHARMACY INVOICE WAS NOT CREATED') ||
    upper.includes('FAILED TO REGISTER-AND-BILL') ||
    upper.includes('COULD NOT READ THE PHARMACY INVOICE')
  ) {
    return 'Billing could not be opened. Your sale is saved. Please try again or contact your administrator.';
  }
  if (upper.includes('SERVICE ALREADY IN BILLING BASKET') || upper.includes('SERVICE ALREADY IN BASKET')) {
    return 'This service is already added to this bill.';
  }
  if (upper.includes('BIL_INV_DUPLICATE_GENERATE')) {
    return 'This bill has already been processed.';
  }
  if (upper.includes('BIL_RCP_DUPLICATE_POST')) {
    return 'This payment has already been processed.';
  }
  if (
    upper.includes('BIL_DISC_UNAUTHORIZED') ||
    upper.includes('BIL_DISC_INVALID_APPROVER') ||
    upper.includes('UNAUTHORIZED_OPERATIONAL_ACTION')
  ) {
    return 'You do not have permission to apply this discount.';
  }
  if (upper.includes('BIL_DISC_EXCESSIVE')) {
    return 'This discount is higher than allowed for your role.';
  }
  if (upper.includes('BIL_DISC_IMMUTABLE_INVOICE') || upper.includes('BIL_DOC_IMMUTABLE')) {
    return 'This bill is already generated. Discount can only be applied before generating the invoice.';
  }
  if (
    upper.includes('CHANGED SINCE') ||
    upper.includes('VERSION MISMATCH') ||
    upper.includes('BIL_DISC_STALE') ||
    upper.includes('BIL_RCP_STALE') ||
    (upper.includes('BIL_OP_CONFLICT') && upper.includes('STALE'))
  ) {
    return BILL_CHANGED;
  }
  if (upper.includes('BIL_INV_EMPTY')) {
    if (upper.includes('NET') || upper.includes('POSITIVE') || upper.includes('ZERO')) {
      return 'The bill amount must be greater than zero.';
    }
    return 'Add at least one service before generating the invoice.';
  }
  if (upper.includes('BIL_RCP_OVERPAYMENT')) {
    return 'Payment is more than the amount due.';
  }
  if (upper.includes('BIL_SHIFT_LOCKED') || upper.includes('BIL_CTR_CLOSED_SESSION')) {
    return 'Your cashier shift is closed. Open a shift before collecting payment.';
  }
  return '';
}

/**
 * @param {object | null | undefined} invoice
 */
export function buildPaymentFingerprint(invoice) {
  if (!invoice?.invoiceId) return null;
  return {
    invoiceId: invoice.invoiceId,
    status: invoice.status,
    amountPaid: Number(invoice.amountPaid ?? 0),
    totalAmount: Number(invoice.totalAmount ?? 0),
    version: invoice.version ?? null,
  };
}

/**
 * @param {ReturnType<typeof buildPaymentFingerprint>} before
 * @param {ReturnType<typeof buildPaymentFingerprint>} after
 */
export function paymentFingerprintChanged(before, after) {
  if (!before || !after) return false;
  if (before.invoiceId !== after.invoiceId) return true;
  if (before.status !== after.status) return true;
  if (before.version != null && after.version != null && before.version !== after.version) {
    return true;
  }
  return Math.abs(before.amountPaid - after.amountPaid) > 0.009;
}

export function isPostCollectCooldownActive(untilMs) {
  return typeof untilMs === 'number' && untilMs > Date.now();
}

export {
  classifyOperationalSupportCode,
  operationalSupportDetail,
  supportGuidanceForCode,
} from './operationalSupportCodes.js';

function extractOperationalErrorMessage(error) {
  if (!error) return '';
  const data = error.response?.data ?? error.data;
  const fromBody = messageFromErrorBody(data);
  if (fromBody) return fromBody;
  if (error.message && !isAxiosStatusPhrase(error.message)) return String(error.message);
  return '';
}

function messageFromErrorBody(data) {
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed || trimmed.startsWith('{') || trimmed.startsWith('[')) return '';
    return trimmed;
  }
  if (!data || typeof data !== 'object') return '';
  if (typeof data.error === 'string' && data.error.trim() && data.error !== '[object Object]') {
    return data.error.trim();
  }
  if (typeof data.message === 'string' && data.message.trim() && data.message !== '[object Object]') {
    return data.message.trim();
  }
  if (data.error && typeof data.error === 'object') {
    if (typeof data.error.message === 'string' && data.error.message.trim()) {
      return data.error.message.trim();
    }
    return '';
  }
  return '';
}

function detailAfterCode(raw) {
  const match = String(raw || '').match(/^(?:BIL|PHM)_[A-Z0-9_]+\s*:\s*(.+)$/i);
  if (!match) return '';
  const detail = match[1].trim();
  if (!detail || /BIL_|\[object Object\]|request failed with status/i.test(detail)) return '';
  return detail.charAt(0).toUpperCase() + detail.slice(1);
}

function isAxiosStatusPhrase(message) {
  return /request failed with status code \d+/i.test(String(message || ''));
}

function isUnsafeCashierText(message) {
  const text = String(message || '').trim();
  if (!text) return true;
  if (text === '[object Object]') return true;
  if (isAxiosStatusPhrase(text)) return true;
  if (/^(?:BIL|PHM)_[A-Z0-9_]+/.test(text)) return true;
  if (text.includes('[object Object]')) return true;
  if (/\bException\b/.test(text) || text.includes('\n\tat ')) return true;
  if (text.startsWith('{') || text.startsWith('[')) return true;
  return false;
}

function errorPath(error) {
  const data = error?.response?.data ?? error?.data;
  const path = data && typeof data === 'object' ? data.path : '';
  return `${error?.config?.url || ''} ${path || ''}`;
}

function isBillingError(error, raw) {
  if (/BIL_/.test(String(raw || ''))) return true;
  return /\/billing\b|api\/v1\/billing/i.test(errorPath(error));
}

function hasForeignApiPath(error) {
  const path = errorPath(error);
  if (!path.trim()) return false;
  return !/\/billing\b|api\/v1\/billing/i.test(path);
}

function matchesOperationalError(error, tokens) {
  const msg = extractOperationalErrorMessage(error).toUpperCase();
  return tokens.some((t) => msg.includes(t));
}

function isNetworkOperationalError(error) {
  if (!error) return false;
  const code = error.code ?? error.response?.status;
  if (code === 'ERR_NETWORK' || code === 'ECONNABORTED') return true;
  if (error.message && /network|timeout|failed to fetch/i.test(error.message)) return true;
  return false;
}

function isRefundReviewInvalidStateMessage(message) {
  const m = String(message || '').toUpperCase();
  return (
    m.includes('BIL_REF_REVIEW_INVALID_STATE') ||
    m.includes('REFUND REVIEW IS ALREADY') ||
    m.includes('ALREADY CLOSED') ||
    m.includes('ALREADY RECOLLECTION')
  );
}
